import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { invoke } from '../tauriBridge';

export interface InstanceInfo {
  id: string;
  name: string;
  owner: string;
  visibility: 'public' | 'private';
  tcp_port: number;
  status: string;
}

interface InstanceStore {
  // Developer identity (persisted locally)
  developerName: string;
  setDeveloperName: (name: string) => void;

  // Server connection (persisted locally)
  serverUrl: string;
  setServerUrl: (url: string) => Promise<void>;

  // Instance list
  instances: InstanceInfo[];
  activeInstanceId: string | null;

  // Actions
  fetchInstances: () => Promise<void>;
  createInstance: (name: string, visibility?: 'public' | 'private') => Promise<InstanceInfo>;
  deleteInstance: (id: string) => Promise<void>;
  updateInstance: (id: string, name?: string, visibility?: 'public' | 'private') => Promise<void>;
  setActiveInstance: (id: string) => void;

  // Derived
  activeInstance: () => InstanceInfo | null;
}

export const useInstanceStore = create<InstanceStore>()(
  persist(
    (set, get) => ({
      developerName: '',
      serverUrl: 'http://nmea.vortexnav.com:8200',
      instances: [],
      activeInstanceId: null,

      setDeveloperName: (name) => set({ developerName: name }),

      setServerUrl: async (url) => {
        await invoke('set_server_url', { url });
        set({ serverUrl: url });
        // Re-fetch instances from new server
        await get().fetchInstances();
      },

      fetchInstances: async () => {
        try {
          const instances = await invoke<InstanceInfo[]>('list_instances');
          set({ instances });

          // Auto-select first instance if none active
          const { activeInstanceId } = get();
          if (!activeInstanceId && instances.length > 0) {
            set({ activeInstanceId: instances[0].id });
          }
          // If active instance was deleted, reset to first
          if (activeInstanceId && !instances.find(i => i.id === activeInstanceId)) {
            set({ activeInstanceId: instances.length > 0 ? instances[0].id : null });
          }
        } catch (err) {
          console.error('Failed to fetch instances:', err);
        }
      },

      createInstance: async (name, visibility = 'public') => {
        const { developerName } = get();
        const info = await invoke<InstanceInfo>('create_instance', {
          owner: developerName,
          name,
          visibility,
        });
        await get().fetchInstances();
        set({ activeInstanceId: info.id });
        return info;
      },

      deleteInstance: async (id) => {
        const { developerName } = get();
        await invoke('delete_instance', {
          instanceId: id,
          requester: developerName,
        });
        await get().fetchInstances();
      },

      updateInstance: async (id, name, visibility) => {
        const { developerName } = get();
        await invoke('update_instance', {
          instanceId: id,
          name: name ?? null,
          visibility: visibility ?? null,
          requester: developerName,
        });
        await get().fetchInstances();
      },

      setActiveInstance: (id) => set({ activeInstanceId: id }),

      activeInstance: () => {
        const { instances, activeInstanceId } = get();
        return instances.find(i => i.id === activeInstanceId) ?? null;
      },
    }),
    {
      name: 'nmea-studio-instance',
      version: 1,
      partialize: (state) => ({
        developerName: state.developerName,
        serverUrl: state.serverUrl,
        activeInstanceId: state.activeInstanceId,
      }),
      migrate: (persisted: unknown, version: number) => {
        const state = persisted as Record<string, unknown>;
        if (version < 1) {
          // Migrate from local server to Neptune
          if (!state.serverUrl || typeof state.serverUrl !== 'string' || state.serverUrl.includes('127.0.0.1') || state.serverUrl.includes('localhost')) {
            state.serverUrl = 'http://nmea.vortexnav.com:8200';
          }
        }
        return state;
      },
    }
  )
);
